export type AuthRole = "admin" | "user" | "viewer";

export interface AuthUserProfile {
  id: string;
  name: string;
  email?: string | null;
  photo?: string;
  role?: AuthRole;
}

export interface StoredAuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface StoredAuthSession extends StoredAuthTokens {
  user: AuthUserProfile;
  issuedAt: number;
  expiresAt: number;
}

const ACCESS_TOKEN_KEYS = ["accessToken", "access_token", "token"];
const REFRESH_TOKEN_KEYS = ["refreshToken"];
const AUTH_OBJECT_KEYS = ["warehouseAuth", "auth", "authTokens"];

function getStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readTokenByKeys(keys: string[]): string | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  for (const key of keys) {
    const raw = storage.getItem(key);
    if (typeof raw === "string" && raw.trim()) {
      return raw.trim();
    }
  }

  return null;
}

function readAuthObjectFromStorage(): Record<string, unknown> | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  for (const key of AUTH_OBJECT_KEYS) {
    const raw = storage.getItem(key);
    if (!raw) {
      continue;
    }

    try {
      const parsed = JSON.parse(raw) as unknown;
      if (isRecord(parsed)) {
        return parsed;
      }
    } catch {
      // ignore malformed JSON in storage and try next key
    }
  }

  return null;
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function asPositiveNumber(value: unknown): number | null {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }
  return num;
}

export function getStoredAccessToken(): string | null {
  const directToken = readTokenByKeys(ACCESS_TOKEN_KEYS);
  if (directToken) {
    return directToken;
  }

  const authObject = readAuthObjectFromStorage();
  if (!authObject) {
    return null;
  }

  return (
    asNonEmptyString(authObject.accessToken) ||
    asNonEmptyString(authObject.access_token) ||
    asNonEmptyString(authObject.token)
  );
}

export function getStoredRefreshToken(): string | null {
  const directToken = readTokenByKeys(REFRESH_TOKEN_KEYS);
  if (directToken) {
    return directToken;
  }

  const authObject = readAuthObjectFromStorage();
  if (!authObject) {
    return null;
  }

  return asNonEmptyString(authObject.refreshToken);
}

export function getStoredUser(): AuthUserProfile | null {
  const authObject = readAuthObjectFromStorage();
  if (!authObject || !isRecord(authObject.user)) {
    return null;
  }

  const id = asNonEmptyString(authObject.user.id);
  const name = asNonEmptyString(authObject.user.name);

  if (!id || !name) {
    return null;
  }

  const role = asNonEmptyString(authObject.user.role);
  const photo = asNonEmptyString(authObject.user.photo);
  const email = asNonEmptyString(authObject.user.email);

  return {
    id,
    name,
    email: email || null,
    photo: photo || undefined,
    role:
      role === "admin" || role === "user" || role === "viewer"
        ? role
        : undefined,
  };
}

export function getStoredSession(): StoredAuthSession | null {
  const authObject = readAuthObjectFromStorage();
  if (!authObject || !isRecord(authObject.user)) {
    return null;
  }

  const user = getStoredUser();
  if (!user) {
    return null;
  }

  const accessToken =
    asNonEmptyString(authObject.accessToken) || getStoredAccessToken();
  const refreshToken =
    asNonEmptyString(authObject.refreshToken) || getStoredRefreshToken();
  const expiresIn = asPositiveNumber(authObject.expiresIn);
  const issuedAt = asPositiveNumber(authObject.issuedAt) || Date.now();
  const expiresAt = asPositiveNumber(authObject.expiresAt);

  if (!accessToken || !refreshToken || !expiresIn) {
    return null;
  }

  return {
    user,
    accessToken,
    refreshToken,
    expiresIn,
    issuedAt,
    expiresAt: expiresAt || issuedAt + expiresIn * 1000,
  };
}

export function setStoredSession(
  input: AuthUserProfile & StoredAuthTokens,
): StoredAuthSession {
  const storage = getStorage();
  const issuedAt = Date.now();
  const expiresAt = issuedAt + input.expiresIn * 1000;

  const session: StoredAuthSession = {
    user: {
      id: input.id,
      name: input.name,
      email: input.email || null,
      photo: input.photo || "",
      role: input.role || "user",
    },
    accessToken: input.accessToken,
    refreshToken: input.refreshToken,
    expiresIn: input.expiresIn,
    issuedAt,
    expiresAt,
  };

  if (!storage) {
    return session;
  }

  const authTokens = {
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    expiresIn: session.expiresIn,
  };

  const auth = {
    ...authTokens,
    user: session.user,
    issuedAt: session.issuedAt,
    expiresAt: session.expiresAt,
  };

  storage.setItem("warehouseAuth", JSON.stringify(auth));
  storage.setItem("auth", JSON.stringify(auth));
  storage.setItem("authTokens", JSON.stringify(authTokens));
  storage.setItem("accessToken", session.accessToken);
  storage.setItem("access_token", session.accessToken);
  storage.setItem("token", session.accessToken);
  storage.setItem("refreshToken", session.refreshToken);

  return session;
}

export function updateStoredTokens(input: StoredAuthTokens): void {
  const user = getStoredUser();

  setStoredSession({
    id: user?.id || "usr_unauthenticated",
    name: user?.name || "Неизвестный пользователь",
    email: user?.email || null,
    photo: user?.photo || "",
    role: user?.role || "user",
    accessToken: input.accessToken,
    refreshToken: input.refreshToken,
    expiresIn: input.expiresIn,
  });
}

export function hasStoredAccessToken(): boolean {
  return Boolean(getStoredAccessToken());
}

export function updateStoredUserProfile(input: Partial<AuthUserProfile>): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  const authObject = readAuthObjectFromStorage();
  if (!authObject || !isRecord(authObject.user)) {
    return;
  }

  const nextUser = {
    ...authObject.user,
    ...(input.id !== undefined ? { id: input.id } : {}),
    ...(input.name !== undefined ? { name: input.name } : {}),
    ...(input.email !== undefined ? { email: input.email } : {}),
    ...(input.photo !== undefined ? { photo: input.photo } : {}),
    ...(input.role !== undefined ? { role: input.role } : {}),
  };

  const nextAuth = {
    ...authObject,
    user: nextUser,
  };

  storage.setItem("warehouseAuth", JSON.stringify(nextAuth));
  storage.setItem("auth", JSON.stringify(nextAuth));
}

export function clearStoredSession(): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  const keysToRemove = [
    ...ACCESS_TOKEN_KEYS,
    ...REFRESH_TOKEN_KEYS,
    "warehouseAuth",
    "auth",
    "authTokens",
  ];

  keysToRemove.forEach((key) => storage.removeItem(key));
}
